import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, Edit, Save, X } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useToast } from '@/components/ui/use-toast';
import { 
  getAllAppRolloutEntries, 
  addAppRolloutEntry, 
  updateAppRolloutEntry, 
  deleteAppRolloutEntry
} from '@/services/appRolloutService';
import { AppRolloutEntry } from '@/types/admin';
import { PersonSelect } from '@/components/good-things/PersonSelect';
import { RecipientTag } from '@/components/good-things/RecipientTag';
import { employeeNamesService } from '@/services/employeeNames';

interface AppRolloutSectionProps {
  // No props needed as it fetches its own data
}

const AppRolloutSection: React.FC<AppRolloutSectionProps> = () => {
  const { toast } = useToast();
  const [entries, setEntries] = useState<AppRolloutEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [editingEntryId, setEditingEntryId] = useState<string | null>(null);
  const [status, setStatus] = useState<{ type: 'success' | 'error', message: string } | null>(null);
  const [people, setPeople] = useState<string[]>([]);
  
  // Form state
  // Create a date object for today
  const today = new Date();
  // Format the date properly to avoid timezone issues
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  
  const [newEntry, setNewEntry] = useState({
    appId: '',
    date: formattedDate,
    phase: '',
    users: [] as string[],
    note: '',
    selectedUser: '' // Temporary state for user selection
  });

  // Load data
  const loadData = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getAllAppRolloutEntries();
      setEntries(data);
      
      // Load employee names for the dropdown
      const names = await employeeNamesService.getEmployeeNames();
      setPeople(names.map(n => n.displayName));
    } catch (error) {
      console.error('Error loading app rollout entries:', error);
      toast({
        title: "Error",
        description: "Failed to load app rollout entries",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Group entries by app
  const entriesByApp = entries.reduce((acc, entry) => {
    if (!acc[entry.appId]) {
      acc[entry.appId] = [];
    }
    acc[entry.appId].push(entry);
    return acc;
  }, {} as Record<string, AppRolloutEntry[]>);

  // Sort entries by date (newest first) within each app
  Object.keys(entriesByApp).forEach(appId => {
    entriesByApp[appId].sort((a: AppRolloutEntry, b: AppRolloutEntry) => b.date.getTime() - a.date.getTime());
  });

  // Get app names (could be moved to a config file)
  const getAppName = (appId: string): string => {
    const appNames: Record<string, string> = {
      'hub': 'Apps Hub',
      'good-things': 'Good Things',
      'sb-search-term-report': 'SB Search Term Report',
      'asin-sales-history': 'ASIN Sales History',
      'amazon-index-checker': 'Amazon Index Checker',
      'pmt2': 'PMT2',
      'pmt3': 'PMT3',
      'ppc-farm': 'PPC Farm',
      'primary-ledger': 'Primary Ledger',
      'days-of-cover': 'Days of Cover',
      'farming-results': 'Farming Results',
      'plms': 'PLMS',
      'sop': 'SOP Central',
      'employee-directory': 'Employee Directory',
      'product-manager': 'Product Manager',
      'dayaway': 'Day Away Board',
      'day-away': 'Day Away Board',
      'ideogram': 'Ideogram',
      'glossary': 'Glossary',
      'inventory': 'Inventory',
      'idea-machine': 'Idea Machine',
      'lms': 'LMS',
      'smart-writer': 'Smart Writer',
      'ai-dev-quest': 'AI Dev Quest'
    };
    
    return appNames[appId] || appId
      .split(/[-_]/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Get list of apps that have entries
  const appsWithEntries = Object.keys(entriesByApp).map(appId => ({
    appId,
    appName: getAppName(appId),
    entries: entriesByApp[appId]
  })).sort((a, b) => a.appName.localeCompare(b.appName));

  // Handle form input changes
  const handleInputChange = (field: string, value: any) => {
    setNewEntry(prev => ({ ...prev, [field]: value }));
  };

  // Handle adding a user to the list
  const handleAddUser = (user: string) => {
    if (user && !newEntry.users.includes(user)) {
      setNewEntry(prev => ({
        ...prev,
        users: [...prev.users, user],
        selectedUser: '' // Clear selection after adding
      }));
    }
  };

  // Handle removing a user from the list
  const handleRemoveUser = (index: number) => {
    setNewEntry(prev => ({
      ...prev,
      users: prev.users.filter((_, i) => i !== index)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newEntry.appId) {
      toast({
        title: "App is required",
        description: "Please select an app",
        variant: "destructive"
      });
      return;
    }
    
    if (!newEntry.date) {
      toast({
        title: "Date is required",
        description: "Please select a date",
        variant: "destructive"
      });
      return;
    }
    
    if (!newEntry.phase) {
      toast({
        title: "Phase is required",
        description: "Please enter a phase",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsLoading(true);
      
      // Parse the date string to get year, month, and day components
      const [year, month, day] = newEntry.date.split('-').map(Number);
      
      // Create a date object using local components to avoid timezone issues
      const localDate = new Date(year, month - 1, day);
      
      // If editing an existing entry
      if (editingEntryId) {
        await updateAppRolloutEntry(editingEntryId, {
          appId: newEntry.appId,
          date: localDate,
          phase: newEntry.phase,
          users: newEntry.users,
          note: newEntry.note
        });
        
        setStatus({
          type: 'success',
          message: 'App rollout entry updated successfully'
        });
        
        setEditingEntryId(null);
      } else {
        // Adding a new entry
        await addAppRolloutEntry({
          appId: newEntry.appId,
          date: localDate,
          phase: newEntry.phase,
          users: newEntry.users,
          note: newEntry.note
        });
        
        setStatus({
          type: 'success',
          message: 'App rollout entry added successfully'
        });
      }
      
      // Reset form
      // Create a date object for today
      const resetToday = new Date();
      // Format the date properly to avoid timezone issues
      const resetYear = resetToday.getFullYear();
      const resetMonth = String(resetToday.getMonth() + 1).padStart(2, '0');
      const resetDay = String(resetToday.getDate()).padStart(2, '0');
      const resetFormattedDate = `${resetYear}-${resetMonth}-${resetDay}`;
      
      setNewEntry({
        appId: '',
        date: resetFormattedDate,
        phase: '',
        users: [],
        note: '',
        selectedUser: ''
      });
      
      // Reload data
      await loadData();
      
      // Show toast
      toast({
        title: editingEntryId ? "Entry Updated" : "Entry Added",
        description: editingEntryId 
          ? "App rollout entry has been updated successfully" 
          : "New app rollout entry has been added successfully",
        variant: "default"
      });
    } catch (error) {
      console.error('Error saving app rollout entry:', error);
      setStatus({
        type: 'error',
        message: 'Failed to save app rollout entry'
      });
      
      toast({
        title: "Error",
        description: "Failed to save app rollout entry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle editing an entry
  const handleEdit = (entry: AppRolloutEntry) => {
    if (entry.id) {
      setEditingEntryId(entry.id);
    }
    
    // Format the date properly to avoid timezone issues
    const year = entry.date.getFullYear();
    const month = String(entry.date.getMonth() + 1).padStart(2, '0');
    const day = String(entry.date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setNewEntry({
      appId: entry.appId,
      date: formattedDate,
      phase: entry.phase,
      users: [...entry.users],
      note: entry.note,
      selectedUser: ''
    });
  };

  // Handle deleting an entry
  const handleDelete = async (entryId: string) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) {
      return;
    }
    
    try {
      setIsLoading(true);
      await deleteAppRolloutEntry(entryId);
      
      setStatus({
        type: 'success',
        message: 'App rollout entry deleted successfully'
      });
      
      // Reload data
      await loadData();
      
      toast({
        title: "Entry Deleted",
        description: "App rollout entry has been deleted successfully",
        variant: "default"
      });
    } catch (error) {
      console.error('Error deleting app rollout entry:', error);
      setStatus({
        type: 'error',
        message: 'Failed to delete app rollout entry'
      });
      
      toast({
        title: "Error",
        description: "Failed to delete app rollout entry. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingEntryId(null);
    
    // Create a date object for today
    const cancelToday = new Date();
    // Format the date properly to avoid timezone issues
    const cancelYear = cancelToday.getFullYear();
    const cancelMonth = String(cancelToday.getMonth() + 1).padStart(2, '0');
    const cancelDay = String(cancelToday.getDate()).padStart(2, '0');
    const cancelFormattedDate = `${cancelYear}-${cancelMonth}-${cancelDay}`;
    
    setNewEntry({
      appId: '',
      date: cancelFormattedDate,
      phase: '',
      users: [],
      note: '',
      selectedUser: ''
    });
  };

  return (
    <Card className="border-none shadow-none bg-transparent">
      <CardContent className="px-0">
        <div className="space-y-6">


          {status && (
            <Alert
              variant={status.type === 'success' ? 'default' : 'destructive'}
              className="mb-4"
            >
              <AlertDescription>{status.message}</AlertDescription>
            </Alert>
          )}

          {/* Add New Entry Form */}
          <div className="bg-muted/30 p-6 rounded-lg border">
            <h3 className="text-lg font-medium mb-4">
              {editingEntryId ? 'Edit Rollout Entry' : 'Add New Rollout Entry'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>App</Label>
                  <Select
                    value={newEntry.appId}
                    onValueChange={(value) => handleInputChange('appId', value)}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select app" />
                    </SelectTrigger>
                    <SelectContent>
                      {appsWithEntries.map((app) => (
                        <SelectItem key={app.appId} value={app.appId}>
                          {app.appName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phase</Label>
                  <Input
                    type="text"
                    value={newEntry.phase}
                    onChange={(e) => handleInputChange('phase', e.target.value)}
                    placeholder="e.g., Alpha, Beta, Production"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>Note</Label>
                  <Textarea
                    value={newEntry.note}
                    onChange={(e) => handleInputChange('note', e.target.value)}
                    placeholder="Optional notes about this rollout phase"
                    className="min-h-[38px]"
                  />
                </div>
              </div>

              {/* Users Selection */}
              <div className="space-y-2">
                <Label>Users</Label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <PersonSelect
                      value={newEntry.selectedUser}
                      onValueChange={(value: string) => {
                        setNewEntry(prev => ({ ...prev, selectedUser: value }));
                        if (value) {
                          handleAddUser(value);
                        }
                      }}
                      people={people}
                      label=""
                      isOptional={true}
                    />
                  </div>
                  
                  <div>
                    <div className="p-2 border rounded-md min-h-[60px] bg-background">
                      {newEntry.users.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {newEntry.users.map((user, index) => (
                            <RecipientTag
                              key={index}
                              name={user}
                              onRemove={() => handleRemoveUser(index)}
                            />
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted-foreground text-sm p-1">No users selected</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                {editingEntryId && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleCancelEdit}
                  >
                    <X className="h-4 w-4 mr-1" />
                    Cancel
                  </Button>
                )}
                <Button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {editingEntryId ? 'Updating...' : 'Adding...'}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      {editingEntryId ? <Save className="h-4 w-4 mr-1" /> : <Plus className="h-4 w-4 mr-1" />}
                      {editingEntryId ? 'Update Entry' : 'Add Entry'}
                    </span>
                  )}
                </Button>
              </div>
            </form>
          </div>

          {/* App Rollout Entries */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium">App Rollout Entries</h3>
            
            {appsWithEntries.length === 0 ? (
              <p className="text-muted-foreground">No app rollout entries found.</p>
            ) : (
              <Accordion type="multiple" className="w-full">
                {appsWithEntries.map((app) => (
                  <AccordionItem key={app.appId} value={app.appId}>
                    <AccordionTrigger className="text-md font-medium hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-4">
                        <span>{app.appName}</span>
                        <span className="text-sm font-normal text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          {app.entries.length} {app.entries.length === 1 ? 'entry' : 'entries'}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      {app.entries.length === 0 ? (
                        <p className="text-muted-foreground p-2">No rollout entries for this app.</p>
                      ) : (
                        <div className="space-y-4 p-2">
                          {app.entries.map((entry: AppRolloutEntry) => (
                            <div 
                              key={entry.id} 
                              className="border rounded-md p-4 bg-card"
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div>
                                  <h4 className="font-medium">
                                    Phase: {entry.phase}
                                  </h4>
                                  <p className="text-sm text-muted-foreground">
                                    Date: {entry.date.toLocaleDateString()}
                                  </p>
                                </div>
                                <div className="flex space-x-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleEdit(entry)}
                                  >
                                    <Edit className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleDelete(entry.id!)}
                                    className="hover:bg-destructive hover:text-destructive-foreground"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              
                              {entry.note && (
                                <div className="mb-2">
                                  <p className="text-sm">{entry.note}</p>
                                </div>
                              )}
                              
                              {entry.users.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium mb-1">Users ({entry.users.length}):</p>
                                  <div className="flex flex-wrap gap-1">
                                    {entry.users.map((user: string, i: number) => (
                                      <span 
                                        key={i} 
                                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary text-secondary-foreground"
                                      >
                                        {user}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AppRolloutSection;
